import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, useRoutes, useLocation, Navigate } from 'react-router-dom';
import { LOCALES, DEFAULT_LOCALE, localePath, detectLocale } from './i18n/config';
import { LanguageProvider } from './i18n/LanguageContext';
import { ContentProvider } from './content';
import Home from './Home';
import Blog from './Blog';
import BlogPost from './BlogPost';
import NotFound from './NotFound';
import LegalPage from './LegalPage';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

/** Holds the page height while a locale's content chunk is in flight. */
function PageLoading() {
  return <div style={{ minHeight: '100vh' }} aria-busy="true" />;
}

/**
 * Every page renders inside its locale's providers. ContentProvider suspends
 * until that locale's article and legal copy has loaded, so the Suspense
 * boundary sits between the two.
 */
function LocalePage({ locale, children }) {
  return (
    <LanguageProvider locale={locale}>
      <Suspense fallback={<PageLoading />}>
        <ContentProvider>{children}</ContentProvider>
      </Suspense>
    </LanguageProvider>
  );
}

/**
 * The root URL is the one place we auto-switch language: a first-time visitor
 * whose browser asks for a language we publish gets sent to it. A stored
 * choice always wins, so switching to English and reloading is not undone.
 * Deep links are left alone — someone arriving on an English article from
 * search should keep reading the page they clicked.
 */
function HomeGate() {
  const target = detectLocale();
  if (target && target !== DEFAULT_LOCALE) {
    return <Navigate to={localePath(target, '/')} replace />;
  }
  return (
    <LocalePage locale={DEFAULT_LOCALE}>
      <Home />
    </LocalePage>
  );
}

function buildRoutes() {
  const routes = [];

  LOCALES.forEach((locale) => {
    const at = (path) => localePath(locale, path);
    const page = (node) => <LocalePage locale={locale}>{node}</LocalePage>;

    routes.push(
      {
        path: at('/'),
        element: locale === DEFAULT_LOCALE ? <HomeGate /> : page(<Home />),
      },
      { path: at('/blog'), element: page(<Blog />) },
      { path: at('/blog/:slug'), element: page(<BlogPost />) },
      { path: at('/terms-and-services'), element: page(<LegalPage doc="terms" />) },
      { path: at('/privacy-policy'), element: page(<LegalPage doc="privacy" />) },
      // Unknown paths under a locale prefix keep that locale's 404.
      { path: locale === DEFAULT_LOCALE ? '*' : `/${locale}/*`, element: page(<NotFound />) }
    );
  });

  return routes;
}

const routes = buildRoutes();

function AppRoutes() {
  return useRoutes(routes);
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
